<template>
  <div
    class="wg-content-plans"
    :class="{ 'wg-content-plans--empty-only': initialLoadDone && silos.length === 0 }"
  >
    <div
      v-if="!initialLoadDone"
      class="wg-content-plans__skeleton"
      aria-busy="true"
      aria-label="Loading content plans"
    >
      <div class="wg-content-plans__skeleton-toolbar">
        <div class="wg-content-plans__skeleton-pills">
          <div v-for="n in 5" :key="'sk-pill-' + n" class="wg-content-plans__skeleton-pill" />
        </div>
        <div class="wg-content-plans__skeleton-sort">
          <div class="wg-content-plans__skeleton-sort-label" />
          <div class="wg-content-plans__skeleton-select" />
        </div>
      </div>
      <div class="wg-content-plans__skeleton-grid">
        <div v-for="n in 4" :key="'sk-card-' + n" class="wg-content-plans__skeleton-card">
          <div class="wg-content-plans__skeleton-card-head">
            <div class="wg-content-plans__skeleton-card-title-row">
              <div class="wg-content-plans__skeleton-line wg-content-plans__skeleton-line--title" />
              <div class="wg-content-plans__skeleton-icon" />
            </div>
            <div class="wg-content-plans__skeleton-line wg-content-plans__skeleton-line--meta" />
            <div class="wg-content-plans__skeleton-line wg-content-plans__skeleton-line--meta wg-content-plans__skeleton-line--short" />
          </div>
          <div class="wg-content-plans__skeleton-chips">
            <div class="wg-content-plans__skeleton-chip" />
            <div class="wg-content-plans__skeleton-chip wg-content-plans__skeleton-chip--wide" />
          </div>
          <div class="wg-content-plans__skeleton-actions">
            <div class="wg-content-plans__skeleton-line wg-content-plans__skeleton-line--link" />
            <div class="wg-content-plans__skeleton-btn" />
          </div>
        </div>
      </div>
    </div>

    <EmptyUI v-else-if="silos.length === 0" :cards="['audit']" :show-footer="false" />

    <template v-else>
      <div class="wg-content-plans__pills" role="tablist" aria-label="Filter content plans">
        <button
          v-for="pill in filterPills"
          v-show="pill.id !== 'issues' || pill.count > 0"
          :key="pill.id"
          type="button"
          role="tab"
          class="wg-content-plans__pill"
          :class="{ 'wg-content-plans__pill--active': activeFilter === pill.id }"
          :aria-selected="activeFilter === pill.id"
          @click="activeFilter = pill.id"
        >
          <span class="wg-content-plans__pill-label">{{ pill.label }}</span>
          <span class="wg-content-plans__pill-count">{{ pill.count }}</span>
        </button>
      </div>

      <div class="wg-content-plans__toolbar">
        <header class="wg-content-plans__intro">
          <h2 class="wg-content-plans__title">The Content Plan area lists the content clusters we have planned for you.</h2>
          <p class="wg-content-plans__lede">
            A content cluster is made up of a main page / keyword and recommended supporting articles. Topics for supporting articles are chosen based on relevancy, search volume and your site’s ability to quickly rank for them.
          </p>
        </header>

        <div class="wg-content-plans__sort">
          <span id="wg-content-plans-sort-label" class="wg-content-plans__sort-label">Sorted by</span>
          <b-dropdown
            aria-role="list"
            class="wg-content-plans-sort"
            position="is-bottom-left"
          >
            <template #trigger>
              <button
                type="button"
                class="wg-content-plans-sort__trigger"
                aria-haspopup="listbox"
                aria-labelledby="wg-content-plans-sort-label wg-content-plans-sort-value"
              >
                <span id="wg-content-plans-sort-value">{{ sortByLabel }}</span>
                <i class="material-icons-outlined wg-content-plans-sort__caret">expand_more</i>
              </button>
            </template>
            <b-dropdown-item
              aria-role="listitem"
              @click.native="sortBy = 'recent'"
            >
              Most recent
            </b-dropdown-item>
            <b-dropdown-item
              aria-role="listitem"
              @click.native="sortBy = 'name'"
            >
              Name (A–Z)
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <div class="wg-content-plans__grid">
        <content-plan-card
          v-for="silo in displayedSilos"
          :key="silo.id"
          :silo="silo"
          :refresh-data="fetchSilos"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { mixin } from '@/components/mixin'
import ContentPlanCard from './ContentPlanCard.vue'
import EmptyUI from './EmptyUI.vue'

function isOrderedLike (silo) {
  return ['paid', 'to_be_paid_by_invoice'].includes(silo?.content_report_order?.order_status)
}

export default {
  name: 'ContentPlansWhiteGlove',
  components: { ContentPlanCard, EmptyUI },
  mixins: [mixin],
  data () {
    return {
      silos: [],
      initialLoadDone: false,
      activeFilter: 'all',
      sortBy: 'recent',
    }
  },
  computed: {
    siteId () {
      return Number(this.$route.params.siteId)
    },
    orderedSilos () {
      return this.silos.filter((s) => isOrderedLike(s))
    },
    notOrderedSilos () {
      return this.silos.filter((s) => !this.orderedSilos.includes(s))
    },
    readyForOrderSilos () {
      return this.notOrderedSilos
    },
    inProgressSilos () {
      return this.orderedSilos.filter((s) => ['in_progress', 'pending'].includes(s?.content_report_order?.fulfillement_status))
    },
    completeSilos () {
      return this.orderedSilos.filter((s) => s?.content_report_order?.fulfillement_status === 'completed')
    },
    issuesSilos () {
      return this.silos.filter((s) => s?.content_report_order?.order_status === 'to_be_paid_by_invoice')
    },
    filterPills () {
      return [
        { id: 'all', label: 'All plans', count: this.silos.length },
        { id: 'ready_for_order', label: 'Ready for order', count: this.readyForOrderSilos.length },
        { id: 'in_progress', label: 'In progress', count: this.inProgressSilos.length },
        { id: 'complete', label: 'Complete', count: this.completeSilos.length },
        { id: 'issues', label: 'Issues', count: this.issuesSilos.length },
      ]
    },
    filteredSilos () {
      switch (this.activeFilter) {
        case 'ready_for_order':
          return this.readyForOrderSilos
        case 'in_progress':
          return this.inProgressSilos
        case 'complete':
          return this.completeSilos
        case 'issues':
          return this.issuesSilos
        default:
          return this.silos
      }
    },
    displayedSilos () {
      const list = [...this.filteredSilos]
      if (this.sortBy === 'name') {
        list.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
        return list
      }
      list.sort((a, b) => {
        const ta = new Date(a.updatedAt || a.createdAt || 0).getTime()
        const tb = new Date(b.updatedAt || b.createdAt || 0).getTime()
        return tb - ta
      })
      return list
    },
    sortByLabel () {
      return this.sortBy === 'name' ? 'Name (A–Z)' : 'Most recent'
    },
  },
  watch: {
    '$store.state.refreshDataAfterCartPurchase' () {
      this.fetchSilos()
    },
    '$store.state.triggerRockyRefresh' () {
      this.handleRockyRefresh()
    },
    siteId (next, prev) {
      if (next !== prev && prev !== undefined) {
        this.fetchSilos()
      }
    },
  },
  mounted () {
    this.fetchSilos()
  },
  methods: {
    handleRockyRefresh () {
      this.fetchSilos({ silent: true })
    },
    fetchSilos (opts = {}) {
      const silent = opts.silent === true
      if (!silent) this.$store.commit('showLoading')
      this.$store
        .dispatch('getSilos', {
          siteId: this.siteId,
          paginate: 0,
          // cards only need content_report_order + the server-computed
          // topLevelKeywordLabel / supportingArticlesCount fields
          includeKeywords: 0,
          includeAiAndPageRunResult: 0,
          includePage: 0,
        })
        .then((response) => {
          this.silos = response.data.data || []
        })
        .finally(() => {
          if (!silent) this.$store.commit('hideLoading')
          this.initialLoadDone = true
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.wg-content-plans {
  padding: 8px 20px 40px;
}

.wg-content-plans--empty-only {
  padding: 0;
}

.wg-content-plans__title {
  margin: 0 0 10px;
  font-size: 1rem;
  font-weight: 700;
  color: var(--v-mainColor-base);
}

.wg-content-plans__lede {
  margin: 0;
  max-width: 920px;
  font-size: 0.85rem;
  line-height: 1.55;
  color: var(--v-mainColor-base);
}

@keyframes wg-content-plans-skeleton-shimmer {
  0% {
    background-position: 100% 0;
  }

  100% {
    background-position: -100% 0;
  }
}

@mixin wg-content-plans-skeleton-shine {
  background: linear-gradient(
    90deg,
    #e8e9ed 0%,
    #f2f3f6 45%,
    #e8e9ed 90%
  );
  background-size: 200% 100%;
  animation: wg-content-plans-skeleton-shimmer 1.35s ease-in-out infinite;
}

.wg-content-plans__skeleton {
  padding: 4px 0 8px;
}

.wg-content-plans__skeleton-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.wg-content-plans__skeleton-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.wg-content-plans__skeleton-pill {
  height: 34px;
  width: 88px;
  border-radius: 999px;
  @include wg-content-plans-skeleton-shine;

  &:nth-child(1) {
    width: 96px;
  }

  &:nth-child(2) {
    width: 132px;
  }

  &:nth-child(3) {
    width: 124px;
  }

  &:nth-child(4) {
    width: 100px;
  }

  &:nth-child(5) {
    width: 72px;
  }
}

.wg-content-plans__skeleton-sort {
  display: flex;
  align-items: center;
  gap: 10px;
}

.wg-content-plans__skeleton-sort-label {
  width: 36px;
  height: 14px;
  border-radius: 6px;
  @include wg-content-plans-skeleton-shine;
}

.wg-content-plans__skeleton-select {
  width: 160px;
  height: 36px;
  border-radius: 8px;
  @include wg-content-plans-skeleton-shine;
}

.wg-content-plans__skeleton-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.wg-content-plans__skeleton-card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
  background: #fff;
  border: 1px solid var(--v-gray12Color-base);
  border-radius: 12px;
}

.wg-content-plans__skeleton-card-head {
  min-width: 0;
}

.wg-content-plans__skeleton-card-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.wg-content-plans__skeleton-line {
  height: 12px;
  border-radius: 6px;
  @include wg-content-plans-skeleton-shine;
}

.wg-content-plans__skeleton-line--title {
  flex: 1;
  height: 18px;
  max-width: 100%;
}

.wg-content-plans__skeleton-line--meta {
  margin-top: 8px;
  width: 100%;
}

.wg-content-plans__skeleton-line--short {
  width: 72%;
  margin-top: 6px;
}

.wg-content-plans__skeleton-line--link {
  width: 96px;
  height: 14px;
}

.wg-content-plans__skeleton-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  @include wg-content-plans-skeleton-shine;
}

.wg-content-plans__skeleton-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.wg-content-plans__skeleton-chip {
  height: 24px;
  width: 72px;
  border-radius: 20px;
  @include wg-content-plans-skeleton-shine;
}

.wg-content-plans__skeleton-chip--wide {
  width: 108px;
}

.wg-content-plans__skeleton-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding-top: 12px;
  margin-top: auto;
  border-top: 1px solid var(--v-gray12Color-base);
}

.wg-content-plans__skeleton-btn {
  width: 120px;
  height: 36px;
  border-radius: 20px;
  @include wg-content-plans-skeleton-shine;
}

@media (max-width: 768px) {
  .wg-content-plans__skeleton-grid {
    grid-template-columns: 1fr;
  }
}

.wg-content-plans__toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.wg-content-plans__pills {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
}

.wg-content-plans__pill {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid var(--v-gray12Color-base);
  background: #fff;
  font: inherit;
  font-size: 0.9rem;
  color: var(--v-mainColor-base);
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;

  &:hover {
    border-color: var(--v-profileAnchorColor-base);
    color: var(--v-profileAnchorColor-base);
  }

  &--active {
    background: #4a4f59;
    color: #fff;
    border-color: #4a4f59;

    &:hover {
      color: #fff;
      border-color: #4a4f59;
    }

    .wg-content-plans__pill-count {
      color: #444;
    }
  }
}

.wg-content-plans__pill-label {
  white-space: nowrap;
}

.wg-content-plans__pill-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f7f5fb;
  padding: 2px 10px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 0.7rem;
}

.wg-content-plans__sort {
  display: flex;
  align-items: center;
  gap: 10px;
}

.wg-content-plans__sort-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--v-mainColor-base);
  width: 70px;
}

::v-deep .wg-content-plans-sort .dropdown-trigger {
  display: inline-flex;
}

::v-deep .wg-content-plans-sort .dropdown-menu {
  min-width: 160px;
}

.wg-content-plans-sort__trigger {
  min-width: 132px;
  height: 28px;
  padding: 0 10px 0 12px;
  border: none;
  border-radius: 4px;
  background: #fff;
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--v-mainColor-base);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  line-height: 1.2;
}

.wg-content-plans-sort__caret {
  flex-shrink: 0;
  font-size: 1.125rem !important;
  color: rgba(0, 0, 0, 0.45);
}

.wg-content-plans__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

@media (max-width: 768px) {
  .wg-content-plans__grid {
    grid-template-columns: 1fr;
  }
}
</style>
